import type { ReactNode } from 'react';
import React from 'react';
import { createPortal } from 'react-dom';

import { sizing } from '../../../common/sizing';
import { useShellLayerScrollContext } from '../../../frame/Shell/utils';
import { styled } from '../../../stitches.config';
import { useFieldErrorMessage } from './useFieldErrorMessage';

const FieldErrorMessageStuck = styled('div', {
  transition: 'opacity 200ms ease-in-out, transform 200ms ease-in-out',

  variants: {
    position: {
      top: {
        top: 0,
        transform: 'translateY(0)',
      },
      bottom: {
        bottom: 0,
        transform: 'translateY(0)',
      },
    },
    visible: {
      true: {
        opacity: 1,
        pointerEvents: 'auto',
      },
      false: {
        opacity: 0,
        pointerEvents: 'none',
        transform: 'translateY(100%)',
      },
    },
    hasScrollContainer: {
      true: {
        position: 'absolute',
        right: sizing.primary,
        left: sizing.primary,
      },
      false: {
        position: 'fixed',
        left: 0,
        right: 0,
      },
    },
  },

  compoundVariants: [
    {
      position: 'top',
      visible: false,
      css: {
        transform: 'translateY(-100%)',
      },
    },
  ],
});
FieldErrorMessageStuck.displayName = 'FieldErrorMessageStuck';

const FieldErrorMessageOriginal = styled('div', {
  position: 'relative',

  variants: {
    isStuck: {
      top: {
        borderRadiusBottom: '$10',
      },
      bottom: {
        borderRadiusTop: '$10',
      },
      none: {},
    },
  },
});
FieldErrorMessageOriginal.displayName = 'FieldErrorMessageOriginal';

export function FieldErrorMessagePortal({ children }: { children: React.ReactNode }) {
  // Find the drawer content container instead of the drawer portal
  const mount = document.querySelector('[data-drawer-content]');

  if (!mount) {
    return null;
  }

  return createPortal(children, mount);
}

interface FieldErrorMessageProps {
  /**
   * The error message to display.
   */
  children: ReactNode;
  /**
   * Scroll container to observe (defaults to window)
   */
  scrollContainer?: HTMLElement | null;
}

function FieldErrorMessageContent({ children, scrollContainer = null }: FieldErrorMessageProps) {
  const contextScrollContainer = useShellLayerScrollContext().scrollContainer;
  const finalScrollContainer = scrollContainer || contextScrollContainer;

  const { isStuck, stickyPosition, originalRef, stuckRef, zIndex } = useFieldErrorMessage({
    scrollContainer: finalScrollContainer,
  });

  const hasScrollContainer = !!finalScrollContainer;

  return (
    <>
      {isStuck && (
        <FieldErrorMessagePortal>
          <FieldErrorMessageStuck
            position={stickyPosition}
            visible={isStuck}
            hasScrollContainer={hasScrollContainer}
            css={{
              zIndex,
              [stickyPosition === 'top' ? 'top' : 'bottom']: 0,
            }}
          >
            <FieldErrorMessageOriginal isStuck={stickyPosition} ref={stuckRef}>
              {children}
            </FieldErrorMessageOriginal>
          </FieldErrorMessageStuck>
        </FieldErrorMessagePortal>
      )}
      <FieldErrorMessageOriginal data-alert-container ref={originalRef}>
        {children}
      </FieldErrorMessageOriginal>
    </>
  );
}

export function FieldErrorMessage(props: FieldErrorMessageProps) {
  return <FieldErrorMessageContent {...props} />;
}
